import { useCallback, useEffect, useRef, useState } from 'react'
import { ApolloClient, gql } from '@apollo/client'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { PlusIcon } from '@/atoms/Icons/PlusIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { PhotonInput } from '@/atoms/PhotonInput'
import { CaptionMD, CaptionSM, ParagraphMD, TitleMD } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { useToast } from '@/molecules/Toast'
import { getWebClient } from '@/services/ApolloClient'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { useUser } from '@/services/UserService'
import {
  CreateDiscountIntentMutation,
  CreateDiscountIntentMutationVariables,
  MoneyCurrencyCode,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'

interface Props {
  open: boolean
  onClose: () => void
  theatricalRelease: TheatricalReleaseResult
  venue: ShowtimesVenue
  handleTrackProductAdded: ({
    price,
    currency,
    quantity,
  }: {
    price: number
    currency: MoneyCurrencyCode
    quantity: number
  }) => Promise<void>
}

export const PresaleVenueIntentModal = ({
  open,
  onClose,
  theatricalRelease,
  venue,
  handleTrackProductAdded,
}: Props) => {
  const { t } = useTranslate('tickets')
  const { user } = useUser()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const { showToast } = useToast()
  const federationClient = getWebClient()
  const nextDiscountId = useRef(1)
  const [discounts, setDiscounts] = useState<{ id: number; value: string; error: string | null }[]>([
    { id: 0, value: '', error: null },
  ])

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email)
    }
  }, [user?.email])

  const handleSubmit = useCallback(async () => {
    try {
      if (!validateEmail(email)) {
        setEmailError(t('pleaseEnterValidEmail', 'Please enter a valid email address'))
        return
      }

      const validCodes = discounts.map((d) => d.value).filter((code) => code.trim() !== '')

      if (validCodes.length === 0) {
        setDiscounts((current) =>
          current.map((d) => ({
            ...d,
            error: t('pleaseEnterAtLeastOneTicketCode', 'Please enter at least one ticket code'),
          })),
        )
        return
      }

      setIsLoading(true)

      const { data, error } = await createDiscountIntentsMutation({
        email,
        codes: validCodes,
        theatricalSlug: theatricalRelease?.theatricalSlug ?? '',
        venueId: venue.id,
        federationClient,
      })

      const errorMessage = error?.message ?? data?.createDiscountIntent?.error?.description

      if (errorMessage) {
        const maybeFaultyCode = errorMessage.split('Could not find active discount for code ')[1]
        if (maybeFaultyCode) {
          setDiscounts((current) =>
            current.map((d) =>
              d.value === maybeFaultyCode ? { ...d, error: t('invalidTicketCode', 'Invalid ticket code') } : d,
            ),
          )
          return
        }
        throw new Error(errorMessage)
      }

      await handleTrackProductAdded({
        price: 12.49 * validCodes.length,
        currency: 'USD',
        quantity: validCodes.length,
      })

      showToast(
        t(
          'thankYouWeWillNotifyYouWhenShowtimesAreAvailable',
          'Thank you! We will notify you when showtimes are available.',
        ),
      )

      onClose()
    } catch (error) {
      logger().error('Failed to track presale venue intent', { error })
      if (error instanceof Error) {
        showToast(error.message)
      } else {
        showToast(t('somethingWentWrongPleaseTryAgain', 'Something went wrong! Please try again.'), {
          showInMillis: 10000,
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    email,
    handleTrackProductAdded,
    showToast,
    t,
    discounts,
    theatricalRelease?.theatricalSlug,
    venue.id,
    federationClient,
    onClose,
  ])

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      panelClasses="!p-0 relative text-center rounded-3xl !w-9/10 !max-w-[360px]"
      transition="appear"
    >
      <div className="absolute right-4 top-4 z-10 cursor-pointer rounded-full bg-core-gray-200 p-2" onClick={onClose}>
        <CloseIcon color="black" size={16} />
      </div>

      <div className="flex max-h-[calc(100dvh-60px)] flex-col items-center overflow-y-auto p-6">
        <div className="flex h-14 w-14 items-center justify-center rounded-full border border-core-warning-200 bg-core-warning-200/30">
          <TicketIcon size={24} color="core-warning-500" />
        </div>
        <TitleMD weight="bold" className="mt-4">
          {t('showtimesComingSoonGiveawayEmpty', 'Showtimes Coming Soon')}
        </TitleMD>
        <ParagraphMD color="core-gray-700" className="mt-4">
          {discounts.length > 1 ? (
            <Translate
              t={t}
              i18nKey="youreClaimingYourTicketsToProjectAtVenue"
              defaults="You're claiming your tickets to <semibold>{{ projectName }}</semibold> at <italic>{{ venueName }}</italic>."
              values={{ projectName: theatricalRelease?.title, venueName: venue.name }}
              components={{ semibold: <i className="font-semibold" />, italic: <i /> }}
            />
          ) : (
            <Translate
              t={t}
              i18nKey="youreClaimingYourTicketToProjectAtVenue"
              defaults="You're claiming your ticket to <semibold>{{ projectName }}</semibold> at <italic>{{ venueName }}</italic>."
              values={{ projectName: theatricalRelease?.title, venueName: venue.name }}
              components={{ semibold: <i className="font-semibold" />, italic: <i /> }}
            />
          )}
        </ParagraphMD>

        <CaptionMD color="core-gray-700" className="my-4">
          {t(
            'enterYourEmailAndCodesToBeNotified',
            'Enter your email and codes to be notified when showtimes are posted.',
          )}
        </CaptionMD>

        <div className="w-flex w-full flex-col">
          <CaptionMD color="core-gray-700" className="mb-1 text-left">
            {t('email', 'Email')}
          </CaptionMD>
          <PhotonInput
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setEmailError(null)
            }}
            placeholder={t('email', 'Email')}
            disabled={isLoading}
            error={!!emailError}
            helperText={emailError ?? undefined}
            className="py-3"
            autoFocus
          />
        </div>

        <div className="mt-3 flex w-full flex-col">
          <CaptionMD color="core-gray-700" className="text-left">
            {t('ticketCodes', 'Ticket Codes')}
          </CaptionMD>
          <div className="flex flex-col gap-2">
            {discounts.map((discount, idx) => (
              <div key={discount.id} className="relative flex w-full gap-2">
                <PhotonInput
                  value={discount.value}
                  placeholder={t('enterTicketCode', 'Enter ticket code')}
                  onChange={(e) => {
                    const newValue = e.target.value
                    setDiscounts((current) =>
                      current.map((d) => (d.id === discount.id ? { ...d, value: newValue, error: null } : d)),
                    )
                  }}
                  error={idx === 0 && !!discount.error}
                  helperText={idx === 0 ? discount.error ?? undefined : undefined}
                  containerClassName="flex-1"
                  className="py-3 pr-10"
                />
                {idx > 0 && (
                  <Button
                    variant="plain"
                    onClick={() => setDiscounts((current) => current.filter((d) => d.id !== discount.id))}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-3"
                  >
                    <CloseIcon color="core-gray-700" size={16} />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button
            variant="gray"
            size="sm"
            onClick={() =>
              setDiscounts((current) => [...current, { id: nextDiscountId.current++, value: '', error: null }])
            }
            disabled={discounts.some((d) => d.value.trim() === '')}
            className="mt-2 flex items-center justify-center gap-1 rounded-lg border border-core-gray-300 py-2"
          >
            <PlusIcon color="core-gray-700" size={12} />
            <CaptionSM weight="semibold" color="core-gray-700">
              {t('addAnotherTicketCode', 'Add Another Ticket Code')}
            </CaptionSM>
          </Button>
        </div>

        <Button
          onClick={handleSubmit}
          variant="black"
          className="mt-4 w-full"
          disabled={isLoading || discounts.length === 0}
        >
          {t('emailMeWhenReady', 'Email Me When Ready')}
        </Button>
      </div>
    </Modal>
  )
}

const validateEmail = (email: string) => {
  return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
}

const CREATE_DISCOUNT_INTENT_MUTATION = gql`
  mutation createDiscountIntent($input: CreateDiscountIntentInput!) {
    createDiscountIntent(input: $input) {
      discountIntents {
        theatricalSlug
      }
      error {
        description
        reference
      }
    }
  }
`

interface CreateDiscountIntentProps {
  email: string
  codes: string[]
  theatricalSlug: string
  venueId: string
  federationClient: ApolloClient
}

const createDiscountIntentsMutation = async ({
  email,
  codes,
  theatricalSlug,
  venueId,
  federationClient,
}: CreateDiscountIntentProps) => {
  const { data, error } = await federationClient.mutate<
    CreateDiscountIntentMutation,
    CreateDiscountIntentMutationVariables
  >({
    mutation: CREATE_DISCOUNT_INTENT_MUTATION,
    variables: {
      input: {
        email,
        discountCodes: codes,
        theatricalSlug,
        intentType: 'WAIT_FOR_VENUE',
        venueId,
      },
    },
  })

  return { data, error }
}
